/**
 * FunnelSelector — trigger + dropdown de funis (selecionar / editar / criar).
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from '../../../components/icons/AppIcon';
import { Funnel } from '../../../types';
import { useFunnelDropdown } from '../hooks/useFunnelDropdown';
import { cn } from '@/lib/utils';

interface FunnelSelectorProps {
    funnels: Funnel[];
    selectedFunnelId: string | null;
    onFunnelChange: (id: string) => void;
    onEditFunnel: () => void;
    onCreateFunnel: () => void;
    /** Se false, oculta editar/criar (member). */
    canManageFunnels?: boolean;
    triggerClassName?: string;
    className?: string;
}

const FOCUS_RING = 'outline-none focus-visible:ring-2 focus-visible:ring-ring/40';
const ITEM_SURFACE =
    'group-hover/item:bg-surface-0 group-hover/item:text-default group-focus-within/item:bg-surface-0 group-focus-within/item:text-default';
const ITEM_FG = 'group-hover/item:text-primary-foreground group-focus-within/item:text-primary-foreground';

/** Lápis branco no hover do item; tooltip “Editar” só no hover/foco do lápis. */
function FunnelEditButton({
    funnelName,
    showCheckAtRest = false,
    className,
    onClick,
}: {
    funnelName: string;
    showCheckAtRest?: boolean;
    className?: string;
    onClick: (e: React.MouseEvent) => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'group/edit relative flex size-9 shrink-0 touch-manipulation items-center justify-center rounded-md text-muted transition-colors',
                ITEM_SURFACE,
                FOCUS_RING,
                className,
            )}
        >
            <span className="sr-only">{`Editar ${funnelName}`}</span>
            <span
                role="tooltip"
                className={cn(
                    'pointer-events-none absolute bottom-full left-1/2 z-tooltip mb-s1 -translate-x-1/2 opacity-0 transition-opacity',
                    'whitespace-nowrap rounded-md bg-gray-900 px-s2 py-s1 text-xs font-medium text-primary-foreground shadow-sm',
                    'group-hover/edit:opacity-100 group-focus-visible/edit:opacity-100',
                )}
            >
                Editar
            </span>
            {showCheckAtRest ? (
                <>
                    <AppIcon
                        name="check"
                        className="size-4 text-primary transition-opacity group-hover/item:opacity-0 group-focus-within/item:opacity-0"
                        aria-hidden
                    />
                    <AppIcon
                        name="edit"
                        className="pointer-events-none absolute size-4 opacity-0 transition-opacity group-hover/item:opacity-100 group-focus-within/item:opacity-100"
                        aria-hidden
                    />
                </>
            ) : (
                <AppIcon name="edit" className="size-4" aria-hidden />
            )}
        </button>
    );
}

export const FunnelSelector: React.FC<FunnelSelectorProps> = ({
    funnels,
    selectedFunnelId,
    onFunnelChange,
    onEditFunnel,
    onCreateFunnel,
    canManageFunnels = true,
    triggerClassName,
    className,
}) => {
    const { isOpen, position, dropdownRef, buttonRef, toggle, close } = useFunnelDropdown();
    const selectedFunnel = funnels.find((f) => f.id === selectedFunnelId);

    const selectFunnel = (id: string) => {
        onFunnelChange(id);
        close();
    };

    const editFunnel = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!canManageFunnels) return;
        if (id !== selectedFunnelId) onFunnelChange(id);
        onEditFunnel();
        close();
    };

    return (
        <div className={cn('relative min-w-0 max-w-full', className)}>
            <button
                ref={buttonRef}
                type="button"
                onClick={toggle}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={selectedFunnel ? `Funil: ${selectedFunnel.name}` : 'Selecionar funil'}
                className={cn(
                    'inline-flex h-9 min-w-0 max-w-full items-center gap-s2 rounded-full border px-s3 text-sm font-semibold transition-colors',
                    FOCUS_RING,
                    selectedFunnel
                        ? 'border-primary-light bg-primary-subtle text-primary'
                        : 'border-border bg-card text-secondary hover:border-primary-light hover:text-primary',
                    triggerClassName,
                )}
            >
                <AppIcon name="filter" className="size-3.5 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-left">{selectedFunnel?.name || 'Selecione'}</span>
                <AppIcon name="chevron-down" className="size-3 shrink-0 opacity-70" aria-hidden />
            </button>

            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        style={{ position: 'fixed', top: position.top, left: position.left }}
                        className="z-[9999] w-[min(100vw-2rem,15rem)] overflow-visible rounded-xl border border-border bg-surface-0 py-s2 shadow-lg"
                    >
                        <ul role="listbox" aria-label="Funis de vendas" className="m-0 list-none overflow-visible p-0">
                            {funnels.map((funnel) => {
                                const isSelected = funnel.id === selectedFunnelId;
                                return (
                                    <li
                                        key={funnel.id}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={cn(
                                            'group/item mx-s2 flex items-center gap-s1 rounded-lg px-s2 py-s1 text-sm transition-colors',
                                            'hover:bg-primary focus-within:bg-primary',
                                            isSelected ? 'font-semibold text-default' : 'font-normal text-secondary',
                                        )}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => selectFunnel(funnel.id)}
                                            className={cn(
                                                'min-h-11 min-w-0 flex-1 touch-manipulation truncate rounded-md px-s2 text-left transition-colors md:min-h-9',
                                                FOCUS_RING,
                                                ITEM_FG,
                                            )}
                                        >
                                            {funnel.name}
                                        </button>

                                        {canManageFunnels ? (
                                            <FunnelEditButton
                                                funnelName={funnel.name}
                                                showCheckAtRest={isSelected}
                                                onClick={(e) => editFunnel(e, funnel.id)}
                                                className={
                                                    isSelected
                                                        ? undefined
                                                        : 'opacity-100 md:opacity-0 md:group-hover/item:opacity-100 md:group-focus-within/item:opacity-100'
                                                }
                                            />
                                        ) : (
                                            isSelected && (
                                                <span
                                                    className={cn('flex size-9 items-center justify-center text-primary', ITEM_FG)}
                                                    aria-hidden
                                                >
                                                    <AppIcon name="check" className="size-4" />
                                                </span>
                                            )
                                        )}
                                    </li>
                                );
                            })}

                            {canManageFunnels && (
                                <li className="mt-s1 border-t border-border pt-s1" role="presentation">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onCreateFunnel();
                                            close();
                                        }}
                                        className={cn(
                                            'min-h-11 w-full touch-manipulation px-s4 py-s3 text-left text-sm font-medium text-primary transition-colors hover:bg-primary-subtle md:min-h-0',
                                            FOCUS_RING,
                                        )}
                                    >
                                        + Novo funil
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>,
                    document.body,
                )}
        </div>
    );
};
